import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import { auth } from "../../firebase";
import { userService } from "../../services/firestoreService";
import menstrualImg from "../../images/content_preferences/Menstrual Wellness.png";
import contraceptionImg from "../../images/content_preferences/Contraception & Family Planning.png";
import fertilityImg from "../../images/content_preferences/Fertility & Reproductive Health.png";
import stiawarenessImg from "../../images/content_preferences/STI Prevention & Awareness.png";
import mentalhealthImg from "../../images/content_preferences/Mental Health & Wellness.png";
import malereproductiveImg from "../../images/content_preferences/Male Reproductive Health.png";
import pregnancyImg from "../../images/content_preferences/Pregnancy Prevention.png";
import generalwellnessImg from "../../images/content_preferences/General Wellness.png";
import communityImg from "../../images/content_preferences/Community Resources.png";

// Content preferences cache for aggressive caching
const contentPreferencesCacheRef = {
  current: null,
  timestamp: null,
  ttl: 30 * 60 * 1000 // 30 minutes TTL
};

// Function to get cached content preferences with TTL check
const getCachedContentPreferences = () => {
  if (!contentPreferencesCacheRef.current) return null;

  // Check if cache is still valid
  if (Date.now() - contentPreferencesCacheRef.timestamp > contentPreferencesCacheRef.ttl) {
    contentPreferencesCacheRef.current = null;
    return null;
  }

  return contentPreferencesCacheRef.current;
};

// Function to update content preferences cache
const updateContentPreferencesCache = (preferences) => {
  contentPreferencesCacheRef.current = preferences;
  contentPreferencesCacheRef.timestamp = Date.now();
};

// Move items array outside component to prevent re-renders
const CONTENT_PREFERENCES_ITEMS = [
  {
    key: "menstrual",
    title: "Menstrual Wellness",
    desc: "Track your cycle, understand hormonal changes, and get personalized wellness tips for every phase of your menstrual journey.",
    img: menstrualImg,
    category: "Health Education",
    color: "#ec4899",
    icon: "🌸",
    features: ["Cycle Tracking", "Symptom Management", "Wellness Tips"]
  },
  {
    key: "contraception",
    title: "Contraception & Family Planning",
    desc: "Explore evidence-based birth control options, compare methods, and make informed decisions about your reproductive future.",
    img: contraceptionImg,
    category: "Preventive Care",
    color: "#3b82f6",
    icon: "🛡️",
    features: ["Method Comparison", "Side Effects", "Effectiveness Rates"]
  },
  {
    key: "fertility",
    title: "Fertility & Reproductive Health",
    desc: "Understand your fertility window, optimize reproductive health, and access resources for family planning goals.",
    img: fertilityImg,
    category: "Health Education",
    color: "#10b981",
    icon: "🌱",
    features: ["Fertility Tracking", "Ovulation Calendar", "Health Optimization"]
  },
  {
    key: "stiawareness",
    title: "STI Prevention & Awareness",
    desc: "Stay informed about STI prevention, recognize symptoms early, and access testing and treatment resources confidentially.",
    img: stiawarenessImg,
    category: "Preventive Care",
    color: "#f59e0b",
    icon: "⚠️",
    features: ["Prevention Tips", "Testing Guide", "Treatment Options"]
  },
  {
    key: "mentalhealth",
    title: "Mental Health & Wellness",
    desc: "Prioritize your emotional well-being with resources for stress management, body positivity, and mental health support.",
    img: mentalhealthImg,
    category: "Mental Health",
    color: "#8b5cf6",
    icon: "🧠",
    features: ["Stress Management", "Self-Care", "Professional Support"]
  },
  {
    key: "malereproductive",
    title: "Male Reproductive Health",
    desc: "Comprehensive information on male reproductive wellness, preventive care, and sexual health education.",
    img: malereproductiveImg,
    category: "Health Education",
    color: "#06b6d4",
    icon: "👨",
    features: ["Health Screening", "Preventive Care", "Wellness Tips"]
  },
  {
    key: "pregnancy",
    title: "Pregnancy Prevention",
    desc: "Access reliable information about pregnancy prevention methods, emergency contraception, and reproductive choices.",
    img: pregnancyImg,
    category: "Preventive Care",
    color: "#ef4444",
    icon: "🚫",
    features: ["Prevention Methods", "Emergency Options", "Decision Support"]
  },
  {
    key: "generalwellness",
    title: "General Wellness",
    desc: "Holistic approach to reproductive health with nutrition, exercise, and lifestyle guidance for optimal well-being.",
    img: generalwellnessImg,
    category: "Wellness",
    color: "#84cc16",
    icon: "✨",
    features: ["Nutrition Guide", "Exercise Tips", "Lifestyle Balance"]
  },
  {
    key: "community",
    title: "Community Outreach Programs",
    desc: "Connect with local health services, support groups, and community programs dedicated to reproductive health advocacy.",
    img: communityImg,
    category: "Community",
    color: "#f97316",
    icon: "🤝",
    features: ["Local Services", "Support Groups", "Health Advocacy"]
  },
];

function ProfileContent({ sharedUserDataCacheRef }) {
  const { language } = useLanguage();
  const t = translations[language];
  const items = CONTENT_PREFERENCES_ITEMS;

  const [settings, setSettings] = useState(
    items.reduce((acc, item) => ({ ...acc, [item.key]: false }), {})
  );
  const isTogglingRef = useRef(false);

  // Fetch user content preferences from Firestore
  useEffect(() => {
    const fetchContentPreferences = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          return;
        }

        // Use dedicated content preferences cache first (30min TTL)
        let preferences = getCachedContentPreferences();

        // If dedicated cache is empty, try the shared user data cache
        if (!preferences) {
          const userData = sharedUserDataCacheRef?.current;
          if (userData && userData.uid === user.uid) {
            preferences = userData.contentPreferences;
            if (preferences) {
              updateContentPreferencesCache(preferences);
            }
          }
        }

        // If still no data, fetch directly from Firestore
        if (!preferences) {
          try {
            const userData = await userService.getUser(user.uid);
            // Warm the shared cache if available
            if (sharedUserDataCacheRef && userData) {
              sharedUserDataCacheRef.current = userData;
            }
            preferences = userData?.contentPreferences;
            if (preferences) {
              updateContentPreferencesCache(preferences);
            }
          } catch (err) {
            // Silent — leave all toggles false on fetch error
            return;
          }
        }

        if (preferences) {
          // Map selectedTopics to toggle states
          let selectedTopicIds = preferences.selectedTopics || [];
          const topics = preferences.topics || [];

          // Check if "Community Outreach Programs" is in topics array (matching CONTENT_PREFERENCES_ITEMS title)
          const hasCommunityOutreach = topics.includes("Community Outreach Programs");

          // Ensure topic ID 9 is included if Community Outreach Program is in topics
          if (hasCommunityOutreach && !selectedTopicIds.includes(9)) {
            selectedTopicIds = [...selectedTopicIds, 9];
          } else if (!hasCommunityOutreach) {
            // Remove topic ID 9 if Community Outreach Program is not in topics
            selectedTopicIds = selectedTopicIds.filter(id => id !== 9);
          }

          // Create mapping from topic IDs to keys
          const topicIdToKey = {
            1: 'menstrual',
            2: 'contraception',
            3: 'fertility',
            4: 'stiawareness',
            5: 'mentalhealth',
            6: 'malereproductive',
            7: 'pregnancy',
            8: 'generalwellness',
            9: 'community'
          };

          // Update settings based on user preferences
          const updatedSettings = {};
          items.forEach(item => {
            // Find the topic ID that corresponds to this item's key
            const topicId = Object.entries(topicIdToKey).find(([id, key]) => key === item.key)?.[0];
            updatedSettings[item.key] = topicId ? selectedTopicIds.includes(parseInt(topicId)) : false;
          });

          setSettings(updatedSettings);
        }
      } catch (error) {
        // Silent error handling for content preferences fetch
      }
    };

    fetchContentPreferences();

    // Listen for storage events from other tabs
    const handleStorageChange = (e) => {
      // Skip if the current tab is the one that made the change
      if (isTogglingRef.current) {
        return;
      }
      if (e.key === 'contentPreferencesUpdated') {
        fetchContentPreferences();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [items, sharedUserDataCacheRef]);

  const toggleSetting = async (key) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return;
      }

      // Set toggling flag to prevent listener conflicts
      isTogglingRef.current = true;

      // Store old value for revert
      const oldValue = settings[key];

      // Update local state first for immediate UI feedback
      const newValue = !settings[key];
      const newSettings = { ...settings, [key]: newValue };
      setSettings(newSettings);

      // Prepare updated preferences
      const selectedTopicIds = [];
      Object.entries(newSettings).forEach(([settingKey, isEnabled]) => {
        if (isEnabled) {
          // Find the topic ID that corresponds to this setting key
          const topicIdToKey = {
            'menstrual': 1,
            'contraception': 2,
            'fertility': 3,
            'stiawareness': 4,
            'mentalhealth': 5,
            'malereproductive': 6,
            'pregnancy': 7,
            'generalwellness': 8,
            'community': 9
          };
          const topicId = topicIdToKey[settingKey];
          if (topicId) {
            selectedTopicIds.push(Number(topicId));
          }
        }
      });

      // Get current preferences to preserve other data
      let userData = sharedUserDataCacheRef?.current;

      // If cache is not available, fetch from Firestore (one-time fetch)
      if (!userData) {
        try {
          userData = await userService.getUser(user.uid);
          // Update cache to prevent future fetches
          if (sharedUserDataCacheRef) {
            sharedUserDataCacheRef.current = userData;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Revert local state on error
          setSettings(prev => ({ ...prev, [key]: oldValue }));
          isTogglingRef.current = false;
          return;
        }
      }

      const currentPreferences = userData?.contentPreferences || {};

      // Update preferences with new selected topics and topics array
      const updatedPreferences = {
        ...currentPreferences,
        selectedTopics: selectedTopicIds,
        topics: items.filter(item => newSettings[item.key]).map(item => item.title),
        timestamp: new Date().toISOString()
      };

      // Save to Firestore
      await userService.updateUser(user.uid, {
        contentPreferences: updatedPreferences
      });

      // Note: We don't update the shared cache here since it's passed as a prop
      // The parent component is responsible for cache management

      // Update dedicated content preferences cache with new preferences
      updateContentPreferencesCache(updatedPreferences);

      // Trigger storage event to notify Learn of changes
      localStorage.setItem('contentPreferencesUpdated', Date.now().toString());

      // Clear toggling flag after successful update
      isTogglingRef.current = false;
    } catch (error) {
      console.error('Error toggling content preference:', error);
      // Revert local state on error using the stored old value
      setSettings(prev => ({ ...prev, [key]: !prev[key] }));
      // Clear toggling flag on error
      isTogglingRef.current = false;
    }
  };

  const handleSelectAll = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return;
      }

      // Set toggling flag to prevent listener conflicts
      isTogglingRef.current = true;

      // Enable all settings
      const allTrue = items.reduce((acc, item) => ({ ...acc, [item.key]: true }), {});
      setSettings(allTrue);

      // Prepare updated preferences with all topic IDs
      const selectedTopicIds = [];
      const topicIdToKey = {
        'menstrual': 1,
        'contraception': 2,
        'fertility': 3,
        'stiawareness': 4,
        'mentalhealth': 5,
        'malereproductive': 6,
        'pregnancy': 7,
        'generalwellness': 8,
        'community': 9
      };

      Object.keys(allTrue).forEach(settingKey => {
        const topicId = topicIdToKey[settingKey];
        if (topicId) {
          selectedTopicIds.push(Number(topicId));
        }
      });

      // Get current preferences to preserve other data
      let userData = sharedUserDataCacheRef?.current;

      // If cache is not available, fetch from Firestore (one-time fetch)
      if (!userData) {
        try {
          userData = await userService.getUser(user.uid);
          // Update cache to prevent future fetches
          if (sharedUserDataCacheRef) {
            sharedUserDataCacheRef.current = userData;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setSettings(prev => ({ ...prev }));
          isTogglingRef.current = false;
          return;
        }
      }

      const currentPreferences = userData?.contentPreferences || {};

      // Update preferences with all selected topics and topics array
      const updatedPreferences = {
        ...currentPreferences,
        selectedTopics: selectedTopicIds,
        topics: items.map(item => item.title),
        timestamp: new Date().toISOString()
      };

      // Save to Firestore
      await userService.updateUser(user.uid, {
        contentPreferences: updatedPreferences
      });

      // Note: We don't update the shared cache here since it's passed as a prop
      // The parent component is responsible for cache management

      // Update dedicated content preferences cache with new preferences
      updateContentPreferencesCache(updatedPreferences);

      // Trigger storage event to notify Learn of changes
      localStorage.setItem('contentPreferencesUpdated', Date.now().toString());

      // Clear toggling flag after successful update
      isTogglingRef.current = false;
    } catch (error) {
      // Revert local state on error
      setSettings(prev => ({ ...prev }));
      isTogglingRef.current = false;
    }
  };

  const handleClearAll = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return;
      }

      // Set toggling flag to prevent listener conflicts
      isTogglingRef.current = true;

      // Disable all settings
      const allFalse = items.reduce((acc, item) => ({ ...acc, [item.key]: false }), {});
      setSettings(allFalse);

      // Get current preferences to preserve other data
      let userData = sharedUserDataCacheRef?.current;

      // If cache is not available, fetch from Firestore (one-time fetch)
      if (!userData) {
        try {
          userData = await userService.getUser(user.uid);
          // Update cache to prevent future fetches
          if (sharedUserDataCacheRef) {
            sharedUserDataCacheRef.current = userData;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setSettings(prev => ({ ...prev }));
          isTogglingRef.current = false;
          return;
        }
      }

      const currentPreferences = userData?.contentPreferences || {};

      // Update preferences with no selected topics and empty topics array
      const updatedPreferences = {
        ...currentPreferences,
        selectedTopics: [],
        topics: [],
        timestamp: new Date().toISOString()
      };

      // Save to Firestore
      await userService.updateUser(user.uid, {
        contentPreferences: updatedPreferences
      });

      // Note: We don't update the shared cache here since it's passed as a prop
      // The parent component is responsible for cache management

      // Update dedicated content preferences cache with new preferences
      updateContentPreferencesCache(updatedPreferences);

      // Trigger storage event to notify Learn of changes
      localStorage.setItem('contentPreferencesUpdated', Date.now().toString());

      // Clear toggling flag after successful update
      isTogglingRef.current = false;
    } catch (error) {
      // Revert local state on error
      setSettings(prev => ({ ...prev }));
      isTogglingRef.current = false;
    }
  };

  return (
    <div className="content-preferences-panel">
      {/* Enhanced Header Section */}
      <div className="content-header">
        <div className="content-header-text">
          <h2 className="content-title">{t.contentPreferences}</h2>
          <p className="content-subtitle">
            {t.personalizeLearningJourney}
          </p>
        </div>
        <div className="content-header-actions">
          <button className="footer-button secondary" onClick={handleClearAll}>
            Clear All
          </button>
          <button className="footer-button primary" onClick={handleSelectAll}>
            Select All
          </button>
        </div>
      </div>

      {/* Enhanced Content Grid */}
      <div className="content-grid">
        {items.map(item => (
          <div 
            key={item.key} 
            className={`content-card ${settings[item.key] ? 'content-card--selected' : ''}`}
            onClick={(e) => {
              // Only toggle if clicking outside the toggle container
              if (!e.target.closest('.toggle-container')) {
                toggleSetting(item.key);
              }
            }}
          >
            {/* Glassmorphism Background */}
            <div className="content-card-glass" />
            
            {/* Card Header with Enhanced Image */}
            <div className="content-card-header">
              <div className="content-image-container">
                <img 
                  src={item.img} 
                  alt={item.title} 
                  className="content-image"
                />
                <div 
                  className="content-overlay"
                  style={{ background: `linear-gradient(135deg, ${item.color}20, ${item.color}40)` }}
                />
                <div className="content-icon">{item.icon}</div>
              </div>
              
              {/* Enhanced Category Badge */}
              <div className="content-category">
                <span className="category-badge" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                  {item.category}
                </span>
              </div>
            </div>

            {/* Enhanced Card Content */}
            <div className="content-card-body">
              <h3 className="content-card-title">{item.title}</h3>
              <p className="content-card-description">{item.desc}</p>
              
              {/* Feature Pills */}
              <div className="content-features">
                {item.features.map((feature, index) => (
                  <span key={index} className="feature-pill" style={{ backgroundColor: `${item.color}10`, color: item.color }}>
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            {/* Toggle switch at top right */}
            <div className="toggle-container">
              <span 
                className="toggle-label" 
                style={{ color: settings[item.key] ? item.color : '#64748b', fontSize: '11px', fontWeight: 600 }}
              >
                {settings[item.key] ? t.enabled : t.disabled}
              </span>
              <button
                className="toggle-switch"
                onClick={() => toggleSetting(item.key)}
                aria-checked={settings[item.key]}
                role="switch"
              >
                <span 
                  className="toggle-slider"
                  style={{ 
                    backgroundColor: settings[item.key] ? item.color : '#e2e8f0'
                  }}
                >
                  <span 
                    className="toggle-thumb"
                    style={{ 
                      transform: settings[item.key] ? 'translateX(22px)' : 'translateX(0)'
                    }}
                  />
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProfileContent;
